import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { FormsModule, NgControl } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { AutofocusDirective } from '../../directives/autofocus/autofocus.directive'
import { NewCustomFormControl } from '../../helpers/new-custom-nested-form'
import { FormError } from '../../interfaces/form-error.interface'
import { FormErrorComponent } from '../form-error/form-error.component'

@Component({
  selector: 'wm-front-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatIconModule, FormErrorComponent, AutofocusDirective, FormsModule],
})
export class InputComponent extends NewCustomFormControl {
  /** The input's label */
  @Input() public label?: string

  /** Draws the required mark after the label (PRENG-8287), instead of a star written in the label's translation */
  @Input() public requiredMark = false

  /** The icon that can be shown at the right of the input */
  @Input() public iconLeft: string

  /** The icon that can be shown at the right of the input */
  @Input() public iconRight: string

  /** Whether or not the icon string is custom i.e not contained in material icons library */
  @Input() public isCustom: boolean

  /** The input's placeholder */
  @Input() public placeholder: string

  /** The type of the input */
  @Input() public type = 'text'

  /** Used to enable the fast deletion with escape or by clicking the X at the right of the input */
  @Input() public fastDelete?: boolean = false

  /** Whether an input should be autofocused when it appears */
  @Input() public autofocus = false

  /** Event dispatched when the icon is clicked */
  @Output() public iconClicked: EventEmitter<Record<string, unknown> | null> =
    new EventEmitter()

  /** The error for the current form control */
  @Input() public error: FormError

  /** Event emitted when the fast delete icon is clicked */
  @Output() public fastDeleted: EventEmitter<void> = new EventEmitter()

  /** Event emitted when the fast delete icon is clicked */
  @Output() public enterPressed: EventEmitter<void> = new EventEmitter()

  /** Event emitted when the input is blurred */
  @Output() public blurEvent: EventEmitter<void> = new EventEmitter()

  @Output() public focusEvent: EventEmitter<void> = new EventEmitter()

  /** Reference to input element */
  @ViewChild('input') public input: ElementRef

  public override ngControl: NgControl

  constructor() {
    const ngControl = inject(NgControl, { optional: true, self: true })
    super(ngControl as NgControl)
    this.ngControl = ngControl as NgControl
  }

  /** Resets the input field on escape
   * @param { KeyboardEvent } event
   */
  public onKeyDown(event: KeyboardEvent): void {
    if (event.code === 'Escape') {
      this.reset()
    } else if (event.code === 'Enter') {
      this.enterPressed.emit()
    }
  }

  /** Called when the icon is clicked */
  public clickOnIcon(): void {
    let params: Record<string, unknown> | null = null

    if (
      this.iconRight?.startsWith('hide') ||
      this.iconRight?.startsWith('show')
    ) {
      if (this.iconRight.startsWith('hide')) {
        this.iconRight = 'show-sm'
        this.type = 'text'
      } else {
        this.iconRight = 'hide-sm'
        this.type = 'password'
      }
      params = {
        icon: this.iconRight,
        type: this.type,
      }
    }

    this.iconClicked.emit(params)
  }

  /** Resets the value of the input */
  public reset(): void {
    this.ngControl.reset()
    this.input.nativeElement.focus()
  }

  /** Will happen when the input will blur */
  public override onBlur(): void {
    this.blurEvent.emit()
    super.onBlur()
  }
}
